// Calendar dates are `yyyy-MM-dd` strings, as they are stored; timestamps
// are ISO 8601 UTC strings. A port of core/shared/utils/dates.dart.

/** A calendar date, `yyyy-MM-dd`. */
export type IsoDate = string;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** The device's calendar date for [now]. */
export function dateOf(now: Date): IsoDate {
  const yyyy = String(now.getFullYear()).padStart(4, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function today(): IsoDate {
  return dateOf(new Date());
}

/** `yyyy-MM-dd` from a stored date or a full timestamp. */
export function toIsoDate(value: string): IsoDate {
  return value.slice(0, 10);
}

function utc(date: IsoDate): number {
  const [y, m, d] = date.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

/** Whole days from [from] to [to]. */
export function daysBetween(from: IsoDate, to: IsoDate): number {
  return Math.round((utc(toIsoDate(to)) - utc(toIsoDate(from))) / 86_400_000);
}

export function addDays(date: IsoDate, days: number): IsoDate {
  const d = new Date(utc(date) + days * 86_400_000);
  return d.toISOString().slice(0, 10);
}

export function parts(date: IsoDate): { year: number; month: number; day: number } {
  const [year, month, day] = toIsoDate(date).split('-').map(Number);
  return { year, month, day };
}

/** `28 Sep 2026`, the design system's date. */
export function formatDate(date: IsoDate): string {
  const { year, month, day } = parts(date);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

/** `28 Sep` this year, otherwise the full date. */
export function formatDateShort(date: IsoDate, now: IsoDate = today()): string {
  const { year, month, day } = parts(date);
  return year === parts(now).year ? `${day} ${MONTHS[month - 1]}` : formatDate(date);
}

/** `Sep`: the short month name for a month number (1–12). */
export function monthName(month: number): string {
  return MONTHS[month - 1];
}

/** The current moment as an ISO 8601 UTC timestamp. */
export function nowStamp(): string {
  return new Date().toISOString();
}

/** Compares two ISO timestamps or dates, earlier first. Parsed, since the
 * phone app writes microseconds and the browser milliseconds. */
export function compareStamps(a: string, b: string): number {
  return Math.sign(Date.parse(a) - Date.parse(b));
}
